import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  format,
  addMonths,
  subMonths,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  CheckCircle,
  X,
  Clock,
  AlertCircle,
  CalendarCheck,
  Lock,
  MapPin,
  HelpCircle,
} from 'lucide-react';
import { formatWeekdayNames } from '@physioedge/shared';
import { api, type Clinic, type ClinicLocation, type Subscription, ApiError } from '../api';
import { ModalOverlay, PageLoader } from '../components/ui';
import { InlinePlans } from '../components/InlinePlans';
import { WhatsAppHelp, useSupportPhone } from '../components/WhatsAppHelp';
import { RequirePhotoModal } from '../components/RequirePhoto';
import { useAuth } from '../auth';
import { FormattedText } from '../lib/simpleMarkup';

interface CalendarWeek {
  scheduleId: string;
  clinicLocationId: string;
  locationName: string;
  title?: string;
  daysOfWeek?: number[];
  weekStartDate: string;
  weekEndDate: string;
  dates: string[];
  slotStartTime: string;
  slotEndTime: string;
  available: boolean;
  isBookedByStudent: boolean;
}

function slotKey(slot: CalendarWeek): string {
  return `${slot.scheduleId}|${slot.weekStartDate}|${slot.slotStartTime}`;
}

function parseDateOnlyLocal(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

function slotDates(slot: CalendarWeek): string[] {
  if (slot.dates?.length) return [...slot.dates].sort();
  return [];
}

function trainingLabel(slot: CalendarWeek): string {
  if (slot.title?.trim()) return slot.title.trim();
  if (slot.daysOfWeek?.length) return formatWeekdayNames(slot.daysOfWeek);
  const dates = slotDates(slot);
  if (dates.length) return dates.map(d => format(parseDateOnlyLocal(d), 'EEE')).join(' · ');
  return 'Internship';
}

function formatSessionDay(date: string): string {
  return format(parseDateOnlyLocal(date), 'EEE d MMM');
}

function formatWeekRange(weekStartDate: string): string {
  const start = parseDateOnlyLocal(weekStartDate);
  const end = parseDateOnlyLocal(weekStartDate);
  end.setDate(end.getDate() + 7);
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM')}`;
}

function dateOnly(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10) : null;
}

function addDaysLocal(iso: string, days: number) {
  const d = parseDateOnlyLocal(iso);
  d.setDate(d.getDate() + days);
  return format(d, 'yyyy-MM-dd');
}

function monthKey(isoOrDate: string | Date): string {
  if (isoOrDate instanceof Date) return format(isoOrDate, 'yyyy-MM');
  return isoOrDate.slice(0, 7);
}

function monthDateFromIso(iso: string): Date {
  const [year, month] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year!, month! - 1, 1);
}

function formatPlanDay(iso: string): string {
  return format(parseDateOnlyLocal(iso.slice(0, 10)), 'd MMM yyyy');
}

function weekFitsPlan(dates: string[], start: string | null, expiry: string | null): boolean {
  if (dates.length === 0) return false;
  const ordered = [...dates].sort();
  const first = ordered[0]!;
  const last = ordered[ordered.length - 1]!;
  if (start && first < start) return false;
  if (expiry && last > expiry) return false;
  return true;
}

function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ampm = h! >= 12 ? 'PM' : 'AM';
  const h12 = h! % 12 || 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

const CONFIRM_WAIT_SECONDS = 10;

function HowToBookDialog({ onClose }: { onClose: () => void }) {
  return (
    <ModalOverlay onBackdropClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="how-to-book-title"
        className="app-dialog bg-white shadow-2xl w-full max-w-sm p-5 sm:p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 id="how-to-book-title" className="font-heading text-lg font-bold text-ink">
            How to book
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ol className="space-y-3 text-sm text-ink mb-5">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-teal text-white text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">1</span>
            <span>Pick the branch.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-teal text-white text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">2</span>
            <span>Tap the days and time you want.</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-teal text-white text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">3</span>
            <span>Press Confirm, wait a few seconds, then press Confirm again.</span>
          </li>
        </ol>
        <p className="text-xs text-ink-soft mb-4">
          Each booking uses 1 week credit. You cannot undo it in the app.
        </p>
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3.5 rounded-radius-sm bg-sand text-white font-semibold text-sm min-h-[48px]"
        >
          Got it
        </button>
      </div>
    </ModalOverlay>
  );
}

interface FinalConfirmDialogProps {
  slot: CalendarWeek;
  clinicName: string;
  clinicPolicy: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error: string;
}

function FinalConfirmDialog({ slot, clinicName, clinicPolicy, onConfirm, onCancel, loading, error }: FinalConfirmDialogProps) {
  const [secondsLeft, setSecondsLeft] = useState(CONFIRM_WAIT_SECONDS);
  const [policyAccepted, setPolicyAccepted] = useState(false);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = window.setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [secondsLeft]);

  return (
    <ModalOverlay
      onBackdropClick={e => { if (e.target === e.currentTarget && !loading) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-week-title"
        className="app-dialog bg-white shadow-2xl w-full max-w-md p-5 sm:p-6 overflow-y-auto overscroll-contain"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 id="book-week-title" className="font-heading text-lg font-bold text-ink pr-4">
            Book this training?
          </h2>
          <button
            onClick={() => { if (!loading) onCancel(); }}
            disabled={loading}
            className="text-ink-faint hover:text-ink transition-colors p-1 rounded-lg hover:bg-gray-100 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="bg-teal-soft rounded-xl p-3 mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-ink-soft shrink-0">Clinic</span>
            <span className="font-medium text-ink text-right">{clinicName}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-ink-soft shrink-0">Branch</span>
            <span className="font-medium text-ink text-right">{slot.locationName || '—'}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-ink-soft shrink-0">Training</span>
            <span className="font-medium text-ink text-right">{trainingLabel(slot)}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-ink-soft shrink-0">Days</span>
            <span className="font-medium text-ink text-right">
              {slotDates(slot).length
                ? slotDates(slot).map(formatSessionDay).join(', ')
                : '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">Time</span>
            <span className="font-medium text-ink">
              {formatTime(slot.slotStartTime)}–{formatTime(slot.slotEndTime)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">Status</span>
            <span className="font-medium text-ink">{slot.available ? 'Available' : 'Unavailable'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">Uses</span>
            <span className="font-semibold text-teal">1 credit</span>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 space-y-2 text-sm text-amber-900">
          <p className="font-semibold text-amber-800">Read this first</p>
          <ul className="space-y-2 list-disc pl-4 text-amber-900/90">
            <li>Once confirmed, this booking <strong>cannot be cancelled in the app</strong>.</li>
            <li>Changes must be requested with the clinic <strong>at least one month in advance</strong>.</li>
            <li>One internship week credit will be deducted immediately.</li>
            <li>Make sure the dates, branch, and clinic are correct.</li>
          </ul>
        </div>

        <label className="flex items-start gap-3 mb-4 text-sm text-ink cursor-pointer">
          <input
            type="checkbox"
            checked={policyAccepted}
            onChange={e => setPolicyAccepted(e.target.checked)}
            className="mt-1 rounded border-line"
          />
          <span>
            <span className="font-medium">I agree to this clinic’s clinical rules.</span>
            <FormattedText text={clinicPolicy} className="mt-1 text-xs text-ink-soft" />
          </span>
        </label>

        {error && (
          <div className="flex items-start gap-2 text-red-600 text-sm bg-red-50 rounded-lg p-3 mb-4">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3.5 rounded-radius-sm border border-line text-ink font-medium text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[48px]"
          >
            Back
          </button>
          <button
            onClick={onConfirm}
            disabled={secondsLeft > 0 || loading || !policyAccepted}
            aria-label={secondsLeft > 0 ? `Wait ${secondsLeft} seconds to confirm` : 'Confirm booking'}
            className={`
              flex-1 py-3.5 rounded-radius-sm font-semibold text-sm min-h-[48px]
              flex items-center justify-center gap-2
              ${secondsLeft > 0 || !policyAccepted
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-sand text-white hover:opacity-90 disabled:hover:opacity-100'}
            `}
          >
            {loading ? (
              <span className="animate-pulse">Booking…</span>
            ) : secondsLeft > 0 ? (
              `Wait ${secondsLeft}s`
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

export function InternshipCalendarPage() {
  const { clinicId } = useParams<{ clinicId: string }>();
  const { me } = useAuth();
  const queryClient = useQueryClient();

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [stagedSlot, setStagedSlot] = useState<CalendarWeek | null>(null);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [showPhotoGate, setShowPhotoGate] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(() => new Set());
  const bookingInFlight = useRef(false);
  const publicSettingsQ = useSupportPhone();
  const defaultClinicPolicy = publicSettingsQ.data?.clinicPolicy
    ?? 'Clinic rules: arrive on time, follow staff instructions, and request changes with the clinic at least one month in advance.';

  const monthStr = format(currentDate, 'yyyy-MM');

  const clinicQ = useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: () => api.get<{ clinic: Clinic; locations: ClinicLocation[] }>(`/api/clinics/${clinicId}`),
    enabled: !!clinicId,
  });

  const subQ = useQuery({
    queryKey: ['student-sub'],
    queryFn: () => api.get<{ subscription: Subscription | null }>('/api/student/subscription'),
  });

  const clinic = clinicQ.data?.clinic;
  const clinicPolicy = (clinic?.studentPolicyText?.trim() || defaultClinicPolicy);
  const locations = clinicQ.data?.locations ?? [];
  const sub = subQ.data?.subscription;
  const todayIso = format(new Date(), 'yyyy-MM-dd');
  const planStart = dateOnly(sub?.planClock?.durationStartsAt ?? sub?.startDate)
    ?? (sub?.status === 'active' && sub.durationWeeks ? todayIso : null);
  const planExpiry = dateOnly(sub?.planClock?.endsAt ?? sub?.expiryDate)
    ?? (planStart && sub?.durationWeeks ? addDaysLocal(planStart, sub.durationWeeks * 7) : null);
  const planStartMonth = planStart ? monthKey(planStart) : null;
  const planExpiryMonth = planExpiry ? monthKey(planExpiry) : null;
  const canGoPrevMonth = !planStartMonth || monthStr > planStartMonth;
  const canGoNextMonth = !planExpiryMonth || monthStr < planExpiryMonth;

  useEffect(() => {
    if (!planStart && !planExpiry) return;
    setCurrentDate(d => {
      const cur = monthKey(d);
      if (planStart && cur < monthKey(planStart)) return monthDateFromIso(planStart);
      if (planExpiry && cur > monthKey(planExpiry)) return monthDateFromIso(planExpiry);
      return d;
    });
  }, [planStart, planExpiry]);

  useEffect(() => {
    if (locations.length === 1 && !selectedLocationId) {
      setSelectedLocationId(locations[0]!.id);
    }
  }, [locations, selectedLocationId]);

  const closeHowTo = useCallback(() => {
    setShowHowTo(false);
  }, []);

  const weeksQ = useQuery({
    queryKey: ['calendar-weeks', clinicId, selectedLocationId, monthStr],
    queryFn: () =>
      api.get<{ weeks: CalendarWeek[] }>(
        `/api/schedules/available?clinicId=${clinicId}&month=${monthStr}&locationId=${selectedLocationId}`,
      ),
    enabled: !!clinicId && !!selectedLocationId,
    staleTime: 30_000,
  });

  const weeks = weeksQ.data?.weeks ?? [];
  const selectedLocation = locations.find(l => l.id === selectedLocationId) ?? null;

  const weekGroups = useMemo(() => {
    const map = new Map<string, CalendarWeek[]>();
    for (const w of weeks) {
      if (!w.isBookedByStudent && !w.available) continue;
      if (
        !w.isBookedByStudent
        && (planStart || planExpiry)
        && !weekFitsPlan(slotDates(w), planStart, planExpiry)
      ) {
        continue;
      }
      const arr = map.get(w.weekStartDate) ?? [];
      arr.push(w);
      map.set(w.weekStartDate, arr);
    }
    return [...map.keys()]
      .sort()
      .map(weekStartDate => {
        const slots = (map.get(weekStartDate) ?? [])
          .slice()
          .sort((a, b) => {
            const byDays = slotDates(a).join(',').localeCompare(slotDates(b).join(','));
            if (byDays) return byDays;
            return a.slotStartTime.localeCompare(b.slotStartTime);
          });
        return { weekStartDate, slots };
      });
  }, [weeks, planStart, planExpiry]);

  const creditCount = sub?.internshipCreditsRemaining ?? 0;
  const freeCredits = Math.max(0, creditCount - (stagedSlot ? 1 : 0));
  const hasCredits = creditCount > 0;
  const canSelect = hasCredits && me?.approvalStatus === 'approved';

  const selectBranch = useCallback((locationId: string) => {
    if (locationId === selectedLocationId) return;
    setSelectedLocationId(locationId);
    setStagedSlot(null);
    setShowFinalConfirm(false);
    setBookingError('');
    setBookingSuccess(false);
    setExpandedWeeks(new Set());
  }, [selectedLocationId]);

  const selectSlot = useCallback((slot: CalendarWeek) => {
    if (!canSelect || slot.isBookedByStudent || !slot.available) return;
    setBookingError('');
    setShowFinalConfirm(false);
    setBookingSuccess(false);
    setStagedSlot(prev => (prev && slotKey(prev) === slotKey(slot) ? null : slot));
  }, [canSelect]);

  const handleConfirmBooking = useCallback(async () => {
    if (!stagedSlot || bookingInFlight.current) return;
    bookingInFlight.current = true;
    setBookingLoading(true);
    setBookingError('');
    try {
      await api.post('/api/bookings', {
        scheduleId: stagedSlot.scheduleId,
        weekStartDate: stagedSlot.weekStartDate,
        slotStartTime: stagedSlot.slotStartTime,
        acceptedClinicPolicy: true,
      });
      setShowFinalConfirm(false);
      setStagedSlot(null);
      setBookingSuccess(true);
      await queryClient.invalidateQueries({ queryKey: ['student-sub'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar-weeks', clinicId, selectedLocationId, monthStr] });
      await queryClient.invalidateQueries({ queryKey: ['student-bookings'] });
    } catch (err) {
      setBookingError(err instanceof ApiError ? err.message : 'Booking failed. Please try again.');
    } finally {
      bookingInFlight.current = false;
      setBookingLoading(false);
    }
  }, [stagedSlot, clinicId, selectedLocationId, monthStr, queryClient]);

  const clearStage = useCallback(() => {
    if (!bookingLoading) {
      setStagedSlot(null);
      setShowFinalConfirm(false);
      setBookingError('');
    }
  }, [bookingLoading]);

  if (clinicQ.isLoading || subQ.isLoading) return <PageLoader variant="calendar" />;
  if (clinicQ.isError) {
    const err = clinicQ.error;
    const notOnPlan = err instanceof ApiError && err.code === 'clinic_not_on_plan';
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Building2 className="w-12 h-12 text-ink-faint mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">
          {notOnPlan ? 'Not on your plan' : 'Clinic not found'}
        </h1>
        <p className="text-ink-soft text-sm mb-4 max-w-md mx-auto">
          {notOnPlan
            ? (err instanceof ApiError ? err.friendlyMessage : 'This clinic is not included in your plan.')
            : 'This clinic is unavailable.'}
        </p>
        <Link to="/clinics" className="text-sand-strong hover:underline text-sm">← Browse clinics</Link>
      </div>
    );
  }
  if (!clinic) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Building2 className="w-12 h-12 text-ink-faint mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Clinic not found</h1>
        <Link to="/clinics" className="text-sand-strong hover:underline text-sm">← Browse clinics</Link>
      </div>
    );
  }

  if (clinic.bookingEnabled === false) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Building2 className="w-12 h-12 text-ink-faint mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Booking paused</h1>
        <p className="text-ink-soft text-sm mb-4 max-w-md mx-auto">
          {clinic.name} is not accepting new internship bookings right now.
        </p>
        <Link to="/clinics" className="text-sand-strong hover:underline text-sm">← Browse clinics</Link>
      </div>
    );
  }

  if (!sub || (sub.status !== 'active' && !(sub.status === 'expired' && !sub.startDate))) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-12">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-teal" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-ink mb-2">You need a plan</h1>
          <p className="text-ink-soft text-sm max-w-md mx-auto">
            You need a plan for clinic weeks and mentor questions.
          </p>
        </div>
        <InlinePlans />
      </div>
    );
  }

  if (sub.status === 'expired' && !sub.startDate) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Lock className="w-12 h-12 text-ink-faint mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Booking is locked</h1>
        <p className="text-ink-soft text-sm mb-4 max-w-md mx-auto">
          You had 14 days to book your first clinic week. Message us and we can reopen your plan.
        </p>
        <Link to="/support" className="text-sand-strong hover:underline text-sm">Message us</Link>
      </div>
    );
  }

  return (
    <div className={`max-w-4xl mx-auto px-4 pb-8 ${stagedSlot ? 'pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-36' : 'pb-32'}`}>
      <div className="sticky top-14 z-30 -mx-4 px-4 py-3 mb-4 bg-bg border-b border-line">
        <div className="flex items-center gap-3">
          <Link to={`/clinics/${clinicId}`} className="text-ink-faint hover:text-ink transition-colors shrink-0" aria-label="Back">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-base sm:text-lg font-bold text-ink leading-tight truncate">
              {clinic.name}
            </h1>
            {me?.approvalStatus !== 'approved' && (
              <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Pending approval
              </p>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="text-right shrink-0">
              <div className="text-sm font-semibold text-teal tabular-nums">{freeCredits} weeks left</div>
              {(planStart || planExpiry) && (
                <div className="text-[10px] text-ink-faint leading-tight mt-0.5">
                  {planStart && planExpiry
                    ? `${formatPlanDay(planStart)} – ${formatPlanDay(planExpiry)}`
                    : planExpiry
                      ? `Until ${formatPlanDay(planExpiry)}`
                      : `From ${formatPlanDay(planStart!)}`}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowHowTo(true)}
            className="shrink-0 text-xs font-medium text-ink-soft hover:text-ink border border-line rounded-full px-2.5 py-1.5 min-h-[36px] flex items-center gap-1"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">How to book</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-line rounded-2xl shadow-card p-4 sm:p-5 mb-6">
        <h2 className="font-heading font-semibold text-ink text-sm mb-1">1. Branch</h2>
        <p className="text-xs text-ink-soft mb-3">Choose where you will train.</p>
        {locations.length === 0 ? (
          <p className="text-sm text-ink-soft">No branches yet.</p>
        ) : (
          <div className="space-y-2">
            {locations.map(loc => {
              const selected = selectedLocationId === loc.id;
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => selectBranch(loc.id)}
                  className={`w-full text-left rounded-xl border-2 px-4 py-3.5 transition-colors min-h-[52px] touch-manipulation ${
                    selected
                      ? 'border-teal bg-teal-soft/60'
                      : 'border-line hover:border-teal/40 hover:bg-surface'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-ink text-sm">{loc.name}</div>
                      <div className="text-xs text-ink-soft mt-0.5">
                        {loc.city} · {loc.addressLine}
                      </div>
                    </div>
                    {selected && <CheckCircle className="w-5 h-5 text-teal shrink-0 mt-0.5" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!hasCredits ? (
        <div className="space-y-4 mb-6">
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
            <div className="text-sm text-amber-700">No credits left. Pick a plan to continue.</div>
          </div>
          <InlinePlans />
        </div>
      ) : me?.approvalStatus !== 'approved' ? (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6">
          <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />
          <div className="text-sm text-amber-700">Approved accounts can book.</div>
        </div>
      ) : null}

      {!selectedLocationId ? (
        <div className="bg-surface border border-dashed border-line rounded-2xl p-8 text-center mb-6">
          <MapPin className="w-8 h-8 text-ink-faint mx-auto mb-3" />
          <p className="text-sm text-ink-soft">Pick a branch first.</p>
        </div>
      ) : (
        <>
          {bookingSuccess && !stagedSlot && (
            <div className="mb-6 flex items-center gap-3 bg-green-50 border border-green-200 rounded-2xl p-4">
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
              <div className="text-sm text-green-800 font-medium">Booked.</div>
            </div>
          )}

          <div className="bg-white border border-line rounded-2xl shadow-card overflow-hidden mb-6">
            <div className="px-4 py-2.5 border-b border-line bg-teal-soft/40 flex items-center gap-2 text-xs text-teal">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium truncate">
                {selectedLocation
                  ? `${selectedLocation.name} — ${selectedLocation.city}`
                  : 'Selected branch'}
              </span>
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface">
              <button
                type="button"
                disabled={!canGoPrevMonth}
                onClick={() => {
                  if (!canGoPrevMonth) return;
                  setStagedSlot(null);
                  setCurrentDate(d => subMonths(d, 1));
                }}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-ink-soft hover:text-ink disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-transparent"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-center">
                <div className="text-[11px] font-medium text-ink-faint uppercase tracking-wide">2. Pick days</div>
                <h2 className="font-heading font-semibold text-ink text-base">
                  {format(currentDate, 'MMMM yyyy')}
                </h2>
              </div>
              <button
                type="button"
                disabled={!canGoNextMonth}
                onClick={() => {
                  if (!canGoNextMonth) return;
                  setStagedSlot(null);
                  setCurrentDate(d => addMonths(d, 1));
                }}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-ink-soft hover:text-ink disabled:opacity-30 disabled:pointer-events-none disabled:hover:bg-transparent"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4">
              {weeksQ.isLoading ? (
                <PageLoader variant="calendar" />
              ) : weekGroups.length === 0 ? (
                <div className="py-8 text-center space-y-3">
                  <p className="text-ink-soft text-sm">
                    No weeks this month. Check next month, or WhatsApp us if you need help.
                  </p>
                  <WhatsAppHelp />
                </div>
              ) : (
                <div className="space-y-3">
                  {weekGroups.map(group => {
                    const open = expandedWeeks.has(group.weekStartDate);
                    return (
                      <div key={group.weekStartDate} className="rounded-xl border border-line overflow-hidden">
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedWeeks(prev => {
                              const next = new Set(prev);
                              if (next.has(group.weekStartDate)) next.delete(group.weekStartDate);
                              else next.add(group.weekStartDate);
                              return next;
                            });
                          }}
                          className="w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-surface text-left touch-manipulation min-h-[48px]"
                        >
                          <span className="text-sm font-semibold text-ink">
                            {formatWeekRange(group.weekStartDate)}
                          </span>
                          <span className="text-xs text-ink-soft shrink-0">
                            {group.slots.length} option{group.slots.length === 1 ? '' : 's'} · {open ? 'Hide' : 'Show'}
                          </span>
                        </button>
                        {open && (
                          <div className="space-y-2 p-3 border-t border-line bg-white">
                            {group.slots.map(slot => {
                              const yours = slot.isBookedByStudent;
                              const selected = !yours && stagedSlot != null && slotKey(stagedSlot) === slotKey(slot);
                              const disabled = yours || !canSelect;
                              const days = slotDates(slot);
                              return (
                                <button
                                  key={slotKey(slot)}
                                  type="button"
                                  disabled={disabled}
                                  onClick={() => selectSlot(slot)}
                                  className={`
                                    w-full text-left rounded-xl border-2 px-4 py-3.5 min-h-[56px]
                                    touch-manipulation transition-colors
                                    ${yours
                                      ? 'border-green-500 bg-green-50 cursor-default'
                                      : selected
                                        ? 'border-teal bg-teal-soft/50'
                                        : 'border-line bg-white hover:border-teal/40 hover:bg-surface'}
                                    ${disabled && !yours ? 'opacity-50 cursor-not-allowed' : ''}
                                  `}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 min-w-0">
                                      <span
                                        className={`
                                          w-4 h-4 rounded-full border-2 shrink-0 mt-1
                                          ${yours
                                            ? 'border-green-600 bg-green-600'
                                            : selected
                                              ? 'border-teal bg-teal'
                                              : 'border-line bg-white'}
                                        `}
                                        aria-hidden
                                      />
                                      <div className="min-w-0">
                                        <div className="text-sm font-semibold text-ink">
                                          {trainingLabel(slot)}
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                          {days.length > 0 ? days.map(d => (
                                            <span
                                              key={d}
                                              className="text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-teal-soft text-teal"
                                            >
                                              {formatSessionDay(d)}
                                            </span>
                                          )) : (
                                            <span className="text-xs text-ink-soft">Days not listed</span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1.5 text-sm text-ink-soft mt-1.5">
                                          <Clock className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                                          {formatTime(slot.slotStartTime)}–{formatTime(slot.slotEndTime)}
                                          {!yours && slot.available ? ' · Available' : ''}
                                        </div>
                                      </div>
                                    </div>
                                    {yours && (
                                      <span className="text-xs font-semibold text-green-700 shrink-0">Yours</span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {stagedSlot && (
        <div className="fixed inset-x-0 z-30 border-t border-line bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.08)] bottom-16 md:bottom-0 pb-[env(safe-area-inset-bottom)]">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="text-[11px] font-medium text-ink-faint uppercase tracking-wide mb-1">3. Confirm</div>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-teal-soft flex items-center justify-center shrink-0">
                <CalendarCheck className="w-4 h-4 text-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-ink text-sm truncate">
                  {trainingLabel(stagedSlot)}
                </div>
                <div className="text-xs text-ink mt-0.5">
                  {slotDates(stagedSlot).map(formatSessionDay).join(', ') || 'Days not listed'}
                </div>
                <div className="text-xs text-ink-soft">
                  {formatTime(stagedSlot.slotStartTime)}–{formatTime(stagedSlot.slotEndTime)}
                  {stagedSlot.available ? ' · Available' : ''}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={clearStage}
                className="flex-1 py-3.5 rounded-radius-sm border border-line text-ink font-medium text-sm hover:bg-gray-50 transition-colors min-h-[48px] touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setBookingError('');
                  if (!me?.hasPhoto) {
                    setShowPhotoGate(true);
                    return;
                  }
                  setShowFinalConfirm(true);
                }}
                className="flex-1 py-3.5 rounded-radius-sm bg-sand text-white font-semibold text-sm hover:opacity-90 transition-opacity min-h-[48px] touch-manipulation"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showHowTo && <HowToBookDialog onClose={closeHowTo} />}

      <RequirePhotoModal
        open={showPhotoGate}
        onClose={() => setShowPhotoGate(false)}
        onDone={() => {
          setShowPhotoGate(false);
          setShowFinalConfirm(true);
        }}
      />

      {showFinalConfirm && stagedSlot && (
        <FinalConfirmDialog
          slot={stagedSlot}
          clinicName={clinic.name}
          clinicPolicy={clinicPolicy}
          onConfirm={handleConfirmBooking}
          onCancel={() => { if (!bookingLoading) { setShowFinalConfirm(false); setBookingError(''); } }}
          loading={bookingLoading}
          error={bookingError}
        />
      )}
    </div>
  );
}
